import React, { useState } from 'react';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Container, i18n } from '../../../../../../symphony';
import { StaticPagesContext } from '../../../../../private/contexts';
import { SlStaticPagesActionTypes, StaticPagesActionsService } from '../../../../../private/services/static-pages/sl-static-pages-actions.service';

import MarketingIcon from '../icons/MarketingIcon';
import DesignIcon from '../icons/DesignIcon';
import DataAnalysisIcon from '../icons/DataAnalysisIcon';
import PMIcon from '../icons/PMIcon';
import SalesIcon from '../icons/Sales';
import CustomerSupportIcon from '../icons/CustomerSupportIcon';
import FinanceIcon from '../icons/FinanceIcon';
import CopywritingIcon from '../icons/CopywritingIcon';
import ResearchIcon from '../icons/ResearchIcon';
import HRIcon from '../icons/HRIcon';

import PlusBlue from '../icons/plusIcons/PlusBlue';
import PlusPurple from '../icons/plusIcons/PlusPurple';
import PlusOrange from '../icons/plusIcons/PlusOrange';
import PlusYellow from '../icons/plusIcons/PlusYellow';
import PlusMagenta from '../icons/plusIcons/PlusMagenta';
import PlusGreen from '../icons/plusIcons/PlusGreen';
import MinusBlue from '../icons/minusIcons/MinusBlue';
import MinusPurple from '../icons/minusIcons/MinusPurple';
import MinusOrange from '../icons/minusIcons/MinusOrange';
import MinusYellow from '../icons/minusIcons/MinusYellow';
import MinusMagenta from '../icons/minusIcons/MinusMagenta';
import MinusGreen from '../icons/minusIcons/MinusGreen';

import s from './SkillItem.scss';

type Skill =
  'Marketing'
  | 'Design'
  | 'Data Analysis'
  | 'Product Management'
  | 'Sales'
  | 'Customer Support'
  | 'Finance'
  | 'Copywriting'
  | 'Research'
  | 'HR';

type SkillColor =
  'brilliant-blue'
  | 'professional-purple'
  | 'outrageous-orange'
  | 'youthful-yellow'
  | 'magnificent-magenta'
  | 'growing-green';

const skillIconsMap: Record<Skill, () => JSX.Element> = {
  Marketing: MarketingIcon,
  Design: DesignIcon,
  'Data Analysis': DataAnalysisIcon,
  'Product Management': PMIcon,
  Sales: SalesIcon,
  'Customer Support': CustomerSupportIcon,
  Finance: FinanceIcon,
  Copywriting: CopywritingIcon,
  Research: ResearchIcon,
  HR: HRIcon,
};

const skillLocalizationMap: Record<Skill, string> = {
  Marketing: 'ai-home.skill-marketing',
  Design: 'ai-home.skill-design',
  'Data Analysis': 'ai-home.skill-data-analysis',
  'Product Management': 'ai-home.skill-product-management',
  Sales: 'ai-home.skill-sales',
  'Customer Support': 'ai-home.skill-customer-support',
  Finance: 'ai-home.skill-finance',
  Copywriting: 'ai-home.skill-copywriting',
  Research: 'ai-home.skill-research',
  HR: 'ai-home.skill-hr',
};

const plusIconsMap: Record<SkillColor, () => JSX.Element> = {
  'brilliant-blue': PlusBlue,
  'professional-purple': PlusPurple,
  'outrageous-orange': PlusOrange,
  'youthful-yellow': PlusYellow,
  'magnificent-magenta': PlusMagenta,
  'growing-green': PlusGreen,
};

const minusIconsMap: Record<SkillColor, () => JSX.Element> = {
  'brilliant-blue': MinusBlue,
  'professional-purple': MinusPurple,
  'outrageous-orange': MinusOrange,
  'youthful-yellow': MinusYellow,
  'magnificent-magenta': MinusMagenta,
  'growing-green': MinusGreen,
};

export interface ISkillItem {
  skill: Skill;
  color: SkillColor;
  text: string;
}

export function SkillItem({ skill, color, text }: ISkillItem) {
  useStyles(s);

  const [expanded, setExpanded] = useState<boolean>(false);

  const SkillIcon = skillIconsMap[skill];
  const PlusIcon = plusIconsMap[color];
  const MinusIcon = minusIconsMap[color];

  const { actions$ } = Container.take(
    StaticPagesContext,
    StaticPagesActionsService,
  );

  const onClick = () => {
    if (!expanded) {
      actions$.next({
        type: SlStaticPagesActionTypes.TrackAILandingPageClick,
        requestType: 'parallel',
        data: {
          trackParams: {
            sectionTitle: 'Skills',
            buttonId: skill,
            buttonLabel: skill,
            clickType: 'EXPAND',
          },
        },
      });
    }
    setExpanded((val) => !val);
  };

  return (
    <div className={classNames('sl-skill-item', color)}>
      <button
        type="button"
        className="sl-skill-item__main"
        onClick={onClick}
      >
        <div className="sl-skill-item__main__title">
          <div className="sl-skill-item__main__title__icon"><SkillIcon /></div>
          <div className="sl-skill-item__main__title__text">{i18n.t(skillLocalizationMap[skill])}</div>
        </div>
        <div className="sl-skill-item__main__title__icon">
          {expanded ? <MinusIcon /> : <PlusIcon />}
        </div>
      </button>
      <div className={classNames('sl-skill-item__text', { expanded })}>
        {text}
      </div>
    </div>
  );
}
